import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { manageExternalConnection } from '../../../services/externalSources';
import ConnectionDialog from './ConnectionDialog';

const DIALECT_LABELS = {
  bigquery: 'BigQuery',
  postgresql: 'Postgres',
  snowflake: 'Snowflake',
  databricks: 'Databricks',
};

function formatHealth(connection) {
  if (connection.deleting) {
    return {
      label: connection.deleting_by ? 'Deleting' : 'Deletion incomplete',
      color: 'warning',
      detail: 'This connection is unavailable. Retry deletion to finish cleanup.',
    };
  }
  if (connection.creating) {
    return {
      label: 'Creating', color: 'warning',
      detail: 'Credentials are being saved. If creation stops, delete the incomplete connection.',
    };
  }
  if (connection.last_error?.message) {
    return {
      label: 'Error',
      color: 'error',
      detail: connection.last_error.message,
    };
  }
  if (connection.last_ok_at) {
    return {
      label: 'Healthy',
      color: 'success',
      detail: `Last tested ${new Date(connection.last_ok_at).toLocaleString()}`,
    };
  }
  return {
    label: 'Untested',
    color: 'default',
    detail: 'No successful test recorded',
  };
}

function ConnectionsManager({ companyId }) {
  const [connections, setConnections] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [messageSeverity, setMessageSeverity] = useState('success');
  // Tests run per connection: several may be in flight at once, and each one's outcome is
  // kept — and shown — under its own connection, so a slow result never overwrites a
  // newer one for a different connection.
  const [testingIds, setTestingIds] = useState(() => new Set());
  const [testResults, setTestResults] = useState({});

  useEffect(() => {
    if (!companyId) return undefined;
    const ref = collection(db, `users/${companyId}/external_connections`);
    return onSnapshot(ref, (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({
        ...docSnap.data(),
        id: docSnap.id,
      }));
      rows.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
      setConnections(rows);
    }, (err) => {
      console.error('Error loading external connections:', err);
      setError('Failed to load source connections');
    });
  }, [companyId]);

  const hasConnections = connections.length > 0;
  const deleteName = useMemo(() => deleteTarget?.name || deleteTarget?.id || '', [deleteTarget]);

  const openCreateDialog = () => {
    setEditingConnection(null);
    setDialogOpen(true);
  };

  const openEditDialog = (connection) => {
    setEditingConnection(connection);
    setDialogOpen(true);
  };

  const handleTest = async (connection) => {
    const { id } = connection;
    const label = connection.name || connection.id;
    setTestingIds((prev) => new Set(prev).add(id));
    setTestResults((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
    let outcome;
    try {
      const result = await manageExternalConnection({ action: 'test', connection_id: id });
      outcome = result.ok
        ? { severity: 'success', text: `${label}: connection test passed.` }
        : { severity: 'warning', text: `${label}: ${result.error?.message || 'connection test did not pass.'}` };
    } catch (err) {
      outcome = { severity: 'error', text: `${label}: ${err.message || 'failed to test connection'}` };
    }
    setTestResults((prev) => ({ ...prev, [id]: outcome }));
    setTestingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setError('');
    setMessage('');
    try {
      setDeletingId(deleteTarget.id);
      await manageExternalConnection({ action: 'delete', connection_id: deleteTarget.id });
      setMessageSeverity('success');
      setMessage('Connection deleted.');
      setDeleteTarget(null);
    } catch (err) {
      setError(err.message || 'Failed to delete connection');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 3 }}>
      <Paper sx={{ p: 3, borderRadius: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h5">Source Connections</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage live read-only SQL sources for external tables.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            New Connection
          </Button>
        </Box>

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          {message && <Alert severity={messageSeverity} onClose={() => setMessage('')}>{message}</Alert>}
          {Object.entries(testResults).map(([id, outcome]) => (
            <Alert
              key={id}
              severity={outcome.severity}
              onClose={() => setTestResults((prev) => {
                const { [id]: _, ...rest } = prev;
                return rest;
              })}
            >
              {outcome.text}
            </Alert>
          ))}
        </Stack>

        {hasConnections ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Dialect</TableCell>
                  <TableCell>Health</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {connections.map((connection) => {
                  const health = formatHealth(connection);
                  const testing = testingIds.has(connection.id);
                  const rowBusy = testing || deletingId === connection.id;
                  const unavailable = connection.deleting || connection.creating;
                  return (
                    <TableRow key={connection.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {connection.name || connection.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {connection.id}
                        </Typography>
                      </TableCell>
                      <TableCell>{DIALECT_LABELS[connection.dialect] || connection.dialect}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip label={health.label} color={health.color} size="small" variant={health.color === 'default' ? 'outlined' : 'filled'} />
                          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420 }} noWrap>
                            {health.detail}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={testing ? 'Testing...' : 'Test'}>
                          <span>
                            <IconButton size="small" aria-label={`Test ${connection.name || connection.id}`} onClick={() => handleTest(connection)} disabled={rowBusy || unavailable}>
                              {testing ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Edit ${connection.name || connection.id}`}
                              onClick={() => openEditDialog(connection)}
                              disabled={rowBusy || unavailable}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={connection.deleting ? 'Retry deletion' : 'Delete'}>
                          <span>
                            <IconButton size="small" color="error" aria-label={`${connection.deleting ? 'Retry deletion of' : 'Delete'} ${connection.name || connection.id}`} onClick={() => setDeleteTarget(connection)} disabled={rowBusy}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No source connections have been created yet.
            </Typography>
          </Paper>
        )}
      </Paper>

      <ConnectionDialog
        open={dialogOpen}
        connection={editingConnection}
        onClose={() => {
          setDialogOpen(false);
          setEditingConnection(null);
        }}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={deletingId ? undefined : () => setDeleteTarget(null)}>
        <DialogTitle>Delete Source Connection</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete {deleteName}? Connections that are used by external tables cannot be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deletingId === deleteTarget?.id}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deletingId === deleteTarget?.id}>
            {deletingId === deleteTarget?.id ? 'Deleting...' : (deleteTarget?.deleting ? 'Retry deletion' : 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ConnectionsManager;
